import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = "https://tbh.ma";
const DEFAULT_IMAGE = `${SITE_URL}/images/Tbh-logo-3d.png`;
const DEFAULT_TITLE = "TBH Morocco | The Big Heart Association | Empowering Communities";
const DEFAULT_DESCRIPTION = "The Big Heart Association (TBH Morocco) is a non-profit organization in Casablanca, Morocco, dedicated to spreading hope, education, and healthcare through the Heartbeat Journey project and humanitarian solidarity actions across rural Morocco.";

/**
 * SEO Component — Manages all meta tags, Open Graph, Twitter Cards, and JSON-LD structured data.
 *
 * Props:
 * - title, description, url, image, type, name (basic meta)
 * - breadcrumbs: [{ name: "Home", url: "/" }, { name: "Blog", url: "/blog" }]
 * - article: { author, datePublished, dateModified, tags } (for blog posts)
 * - event: { name, startDate, endDate, location, description, image } (for events)
 * - faq: [{ question: "...", answer: "..." }] (for FAQ schema)
 * - noOrganizationSchema: true (to skip org schema on sub-pages)
 */
const SEO = ({
  title,
  description,
  name,
  type,
  url,
  image,
  breadcrumbs,
  article,
  event,
  faq,
  noOrganizationSchema,
}) => {
  const seo = {
    title: title || DEFAULT_TITLE,
    description: description || DEFAULT_DESCRIPTION,
    type: type || "website",
    url: url ? `${SITE_URL}${url}` : SITE_URL,
    image: image || DEFAULT_IMAGE,
  };

  // ── Organization + LocalBusiness Schema ──
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "NGO",
    "@id": `${SITE_URL}/#organization`,
    "name": "The Big Heart Association",
    "alternateName": ["TBH Morocco", "Association Grand Coeur", "جمعية القلب الكبير"],
    "url": SITE_URL,
    "logo": DEFAULT_IMAGE,
    "image": DEFAULT_IMAGE,
    "description": DEFAULT_DESCRIPTION,
    "foundingDate": "2022-02",
    "email": "asso.thebigheart@tbh.ma",
    "telephone": "+212777794712",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Dar Chabab En-Najma, Hay Hassani",
      "addressLocality": "Casablanca",
      "addressCountry": "MA"
    },
    "location": {
      "@type": "Place",
      "name": "Dar Chabab En-Najma",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Hay Hassani",
        "addressLocality": "Casablanca",
        "addressCountry": "MA"
      }
    },
    "areaServed": {
      "@type": "Country",
      "name": "Morocco"
    },
    "sameAs": [
      "https://www.linkedin.com/company/association-tbh",
      "https://www.instagram.com/association.tbh_/",
      "https://facebook.com/association.tbh",
      "https://twitter.com/AssociationTbh"
    ],
    "knowsAbout": [
      "Education in rural Morocco",
      "Medical caravans",
      "School renovation",
      "Humanitarian aid",
      "Community development"
    ],
    "slogan": "Smiling together, creating happiness forever"
  };

  // ── BreadcrumbList Schema ──
  const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `${SITE_URL}${crumb.url}`
    }))
  } : null;

  // ── BlogPosting Schema ──
  const articleSchema = article ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": seo.title,
    "description": seo.description,
    "image": seo.image,
    "author": {
      "@type": "Organization",
      "name": article.author || "The Big Heart Association",
      "url": SITE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "The Big Heart Association",
      "logo": { "@type": "ImageObject", "url": DEFAULT_IMAGE }
    },
    "datePublished": article.datePublished,
    "dateModified": article.dateModified || article.datePublished,
    "mainEntityOfPage": { "@type": "WebPage", "@id": seo.url },
    ...(article.tags && { "keywords": article.tags.join(", ") })
  } : null;

  // ── Event Schema ──
  const eventSchema = event ? {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": event.name,
    "startDate": event.startDate,
    ...(event.endDate && { "endDate": event.endDate }),
    "location": {
      "@type": "Place",
      "name": event.location,
      "address": event.location
    },
    "description": event.description || seo.description,
    "image": event.image || seo.image,
    "organizer": {
      "@type": "Organization",
      "name": "The Big Heart Association",
      "url": SITE_URL
    },
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode"
  } : null;

  // ── FAQ Schema ──
  const faqSchema = faq && faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  return (
    <Helmet>
      {/* Standard metadata */}
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <link rel="canonical" href={seo.url} />

      {/* Language alternates */}
      <link rel="alternate" hrefLang="en" href={seo.url} />
      <link rel="alternate" hrefLang="fr" href={seo.url} />
      <link rel="alternate" hrefLang="x-default" href={seo.url} />

      {/* Open Graph / Facebook / LinkedIn */}
      <meta property="og:type" content={article ? "article" : seo.type} />
      <meta property="og:site_name" content="The Big Heart Association" />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="fr_FR" />
      <meta property="og:locale:alternate" content="ar_MA" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@AssociationTbh" />
      <meta name="twitter:creator" content={name || "@AssociationTbh"} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />

      {/* Organization Schema (on homepage only by default) */}
      {!noOrganizationSchema && (
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      )}

      {/* Breadcrumb Schema */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {/* Article / BlogPosting Schema */}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}

      {/* Event Schema */}
      {eventSchema && (
        <script type="application/ld+json">
          {JSON.stringify(eventSchema)}
        </script>
      )}

      {/* FAQ Schema */}
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
